/**
 * Savings simulator — monthly compounding.
 *
 * Each month the balance earns `annualRate / 12`, then the monthly deposit is
 * added (deposit at month end):
 *   B₀ = initial
 *   Bₘ = Bₘ₋₁ × (1 + r) + deposit,   r = annualRate / 100 / 12
 */

export interface SimulationInput {
  /** Montant initial (FCFA). */
  initial: number;
  /** Dépôt mensuel (FCFA). */
  monthlyDeposit: number;
  /** Taux annuel, en % (8 = 8 %/an). */
  annualRate: number;
  /** Durée totale, en mois. */
  months: number;
}

export interface SimulationRow {
  /** Période écoulée, en mois (fin de l'année ou fin de la durée). */
  month: number;
  /** Total versé depuis le départ (initial + dépôts). */
  contributed: number;
  /** Intérêts cumulés. */
  interest: number;
  /** Capital à la fin de la période. */
  balance: number;
}

export interface SimulationResult {
  finalBalance: number;
  totalContributed: number;
  totalInterest: number;
  /** One row per year, plus a final row when the duration is not whole years. */
  rows: SimulationRow[];
}

export function simulate(input: SimulationInput): SimulationResult {
  const r = input.annualRate / 100 / 12;
  let balance = input.initial;
  let contributed = input.initial;
  const rows: SimulationRow[] = [];

  for (let m = 1; m <= input.months; m++) {
    balance = balance * (1 + r) + input.monthlyDeposit;
    contributed += input.monthlyDeposit;
    if (m % 12 === 0 || m === input.months) {
      rows.push({ month: m, contributed, interest: balance - contributed, balance });
    }
  }

  return {
    finalBalance: balance,
    totalContributed: contributed,
    totalInterest: balance - contributed,
    rows,
  };
}
